import { currencySymbols, platform, urlViews, websiteCurrencies, websiteFlagKeys } from '@va/constants';
import { URL_VIEW } from '@va/types/website';
import { WIX_EMBEDDED_SCRIPT_UNIX } from '@va/wix/shared/constants';
import Config from 'Config';
import moment from 'moment';
import { createSelector } from 'reselect';
import { activeWebsiteSelectors } from './active-website';

export const getNotificationsCardOptions = () => {
  return {
    data: [
      {
        premium: true,
        value: 0,
        type: 'daily',
      },
      {
        premium: true,
        value: 1,
        type: 'weekly',
      },
      {
        premium: false,
        value: 2,
        type: 'monthly',
      },
      {
        premium: false,
        value: 3,
        type: 'never',
      },
    ],
  };
};

/**
 * @deprecated
 * Use {@link activeWebsiteSelectors.selectCoreInfo} instead
 */
export const getWebsite = activeWebsiteSelectors.selectCoreInfo;

export const getUrlViewFlag = (state: any) =>
  state.getIn(['core', 'website', 'frontendFlags', 'urlView'], urlViews.TITLE) as URL_VIEW;

export const getWebsiteLastCompleteOnboardingStep = createSelector([getWebsite], (website) => {
  return website?.frontendFlags?.[websiteFlagKeys.LAST_COMPLETED_ONBOARDING_STEP] as number | undefined;
});

export const getBusinessSector = createSelector([getWebsite], (website) => website.businessSector);
export const getFirstWeekDay = createSelector([getWebsite], (website) => website.firstWeekDay);
export const getEmail = createSelector([getWebsite], (website) => website.email);
export const getWebsiteStatus = createSelector([getWebsite], (website) => website.status);
export const getWebsitePlatform = createSelector([getWebsite], (website) => website.platform);
export const selectWebsiteCustomSnippetDomain = createSelector([getWebsite], (website) => website.snippetCustomDomain);
export const getWebsiteCurrency = createSelector([getWebsite], (website) => website.currency || 'EUR');

export const getWebsiteCurrencySymbol = createSelector([getWebsiteCurrency], (currency) => {
  const currencyItem = websiteCurrencies.find((item) => item.code === currency);
  if (!currencyItem) return '€';
  if (currencyItem.symbol) return currencyItem.symbol;
  return currencyItem.code;
});
export const getConversionCurrency = createSelector([getWebsite], (website) => website.conversionCurrency || 'USD');
export const getConversionCurrencySymbol = createSelector([getConversionCurrency], (currency) => {
  return currencySymbols[currency] || currency;
});
export const getWebsiteInstallDate = createSelector([getWebsite], (website) => moment(website.installDate * 1000));
export const getWebsiteLastInstallDate = createSelector([getWebsite, getWebsiteInstallDate], (website, installDate) => {
  // In case of a reinstall, this gets updated so that installDate can remain unchanged, as there are other validations
  if (!website?.lastInstallDate) {
    return installDate;
  }
  return moment(website.lastInstallDate * 1000);
});
export const isDataResetRequested = createSelector([getWebsite], (website) => website.resetDataRequested);
export const getWebsiteFrontendFlags = createSelector([getWebsite], (website) => website?.frontendFlags ?? {});

/** @type {(state) => number | undefined}*/
export const getLastSeenFreeTrialModal = createSelector(getWebsiteFrontendFlags, (flags) => {
  return flags[websiteFlagKeys.LAST_SEEN_FREE_TRIAL_MODAL];
});

export const isWebsiteOnboardingCompleted = createSelector(
  [getWebsite],
  (website) => !!website?.frontendFlags?.[websiteFlagKeys.ONBOARDING_COMPLETED],
);

export const isWhiteLabelEnabled = createSelector([getWebsite], (website) => website.whiteLabel);
export const isNewWebsite = createSelector([getWebsite], (website) =>
  moment(website.installDate * 1000).isAfter((Config.clickPathDeprecatedReleaseDate ?? 0) * 1000),
);

export const isWixWebsite = createSelector([getWebsitePlatform], (websitePlatform) => websitePlatform === platform.WIX);
export const isFreeWixWebsite = createSelector(
  [getWebsite],
  (website) => website?.platform === platform.WIX && !website.isPremium,
);

export const isWebsiteInProgress = createSelector([getWebsite], (website) => website?.inProgress);

export const websiteReviewStatus = createSelector([getWebsite], (website) => website.reviewStatus);

export const getAiOnboardingCompleteFlag = createSelector([getWebsiteFrontendFlags], (flags) => {
  return flags[websiteFlagKeys.AI_ONBOARDING_COMPLETED];
});

export const getTrendIntelFirstEnabledAt = createSelector(
  [getWebsiteFrontendFlags],
  (flags) => flags[websiteFlagKeys.TREND_INTEL_FIRST_ENABLED_AT] as number | undefined,
);

export const isEmbeddedScriptEnabled = createSelector(
  [isWixWebsite, getWebsiteLastInstallDate],
  (isWixWebsite, lastInstallDate) => {
    if (!isWixWebsite || !WIX_EMBEDDED_SCRIPT_UNIX) {
      return false;
    }

    return lastInstallDate.unix() > WIX_EMBEDDED_SCRIPT_UNIX;
  },
);

export const hasSsrCodeInstalled = createSelector(
  [getWebsite, isEmbeddedScriptEnabled],
  (website, hasEmbeddedScript) => {
    if (hasEmbeddedScript) return true;

    const fFlags = website.frontendFlags;
    if (!fFlags) return false;

    return ((fFlags?.['acceptedBetaSsr'] || fFlags?.['acceptedSsr']) && fFlags?.['ssrCodeInstalled']) as boolean;
  },
);
